import { c as _c } from "react/compiler-runtime";
import { EMAIL_PATTERN, PHONE_PATTERN } from "@justhome/common/constants";
import { useIsMobile } from "@justhome/common/hooks";
import { Lead } from "@justhome/api-core";
import { FormLegalDisclaimer, InlineNotification, TextField } from "@justhome/react-ui";
import { useApplicationTranslation } from "@lib/hooks/translations";
import useLanguage from "@lib/hooks/useLanguage";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import { YesNo } from "@lib/options";
import clsx from "clsx";
import { FC, MouseEventHandler, ReactNode, useEffect } from "react";
import { DeepPartial, FormProvider, useForm } from "react-hook-form";
import FormButtons from "./FormButtons";
export type LeadWallFormValue = Pick<Lead, "email" | "firstName" | "lastName" | "phone"> & {
  explicitOptIn?: boolean;
  purchasePrice?: number;
  postalCode?: string;
  realEstateAgentFeePercentage?: string;
  hasRealEstateAgentFee?: YesNo;
};
interface LeadWallFormProps {
  defaultValues?: DeepPartial<LeadWallFormValue>;
  onSubmit: (value: LeadWallFormValue) => void;
  isLoading: boolean;
  isError: boolean;
  onPrevious?: MouseEventHandler;
  className?: string;
  isEmbedded?: boolean;
  propertyFields?: ReactNode;
  submitLabel?: string;
  emailHint?: string;
}
const LeadWallForm: FC<LeadWallFormProps> = t0 => {
  const $ = _c(96);
  const {
    defaultValues,
    onSubmit,
    isLoading,
    isError,
    onPrevious,
    className,
    isEmbedded: t1,
    propertyFields,
    submitLabel,
    emailHint
  } = t0;
  const isEmbedded = t1 === undefined ? false : t1;
  const {
    t
  } = useApplicationTranslation();
  const locale = useLanguage();
  let t2;
  if ($[0] !== defaultValues) {
    t2 = {
      defaultValues: {
        ...defaultValues
      },
      mode: "onBlur"
    };
    $[0] = defaultValues;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const methods = useForm(t2);
  const {
    control,
    handleSubmit,
    setValue,
    register,
    formState: t3
  } = methods;
  const {
    errors
  } = t3;
  const isMobile = useIsMobile();
  const {
    partner
  } = usePartnerContext();
  let t4;
  let t5;
  if ($[2] !== partner || $[3] !== setValue) {
    t4 = () => {
      setValue("explicitOptIn", !partner);
    };
    t5 = [setValue, partner];
    $[2] = partner;
    $[3] = setValue;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== onSubmit) {
    t6 = values => {
      const {
        explicitOptIn: _,
        ...filteredValues
      } = values;
      onSubmit(filteredValues);
    };
    $[6] = onSubmit;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const onSubmitWrapper = t6;
  const isPartner = !!partner;
  let t7;
  if ($[8] !== handleSubmit || $[9] !== onSubmitWrapper) {
    t7 = handleSubmit(onSubmitWrapper);
    $[8] = handleSubmit;
    $[9] = onSubmitWrapper;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== className) {
    t8 = clsx(className, "flex flex-col m:grid grid-cols-2 items-start gap-2s");
    $[11] = className;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== propertyFields) {
    t9 = propertyFields && <div className="col-span-2 w-full space-y-m">{propertyFields}</div>;
    $[13] = propertyFields;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = !isMobile;
  let t11;
  if ($[15] !== register || $[16] !== t) {
    t11 = register("email", {
      required: t("error_fill_in"),
      pattern: {
        value: EMAIL_PATTERN,
        message: t("email_validation_error", {
          ns: "common"
        })
      }
    });
    $[15] = register;
    $[16] = t;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t) {
    t12 = t("opt_in_form_email_label");
    $[18] = t;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t) {
    t13 = t("opt_in_form_email_placeholder");
    $[20] = t;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = errors.email?.message;
  let t15;
  if ($[22] !== emailHint || $[23] !== isLoading || $[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <TextField data-testid="email" autoFocus={t10} size="md" disabled={isLoading} {...t11} label={t12} placeholder={t13} errorMessage={t14} hint={emailHint} inputMode="email" autoCapitalize="off" enterKeyHint="next" />;
    $[22] = emailHint;
    $[23] = isLoading;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== register || $[31] !== t) {
    t16 = register("firstName", {
      required: t("error_fill_in")
    });
    $[30] = register;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t) {
    t17 = t("opt_in_form_first_name_label");
    $[33] = t;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t) {
    t18 = t("opt_in_form_first_name_placeholder");
    $[35] = t;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const t19 = errors.firstName?.message;
  let t20;
  if ($[37] !== isLoading || $[38] !== t16 || $[39] !== t17 || $[40] !== t18 || $[41] !== t19) {
    t20 = <TextField data-testid="firstName" disabled={isLoading} {...t16} label={t17} placeholder={t18} errorMessage={t19} enterKeyHint="next" />;
    $[37] = isLoading;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== register || $[44] !== t) {
    t21 = register("lastName", {
      required: t("error_fill_in")
    });
    $[43] = register;
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t) {
    t22 = t("opt_in_form_last_name_label");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t("opt_in_form_last_name_placeholder");
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = errors.lastName?.message;
  let t25;
  if ($[50] !== isLoading || $[51] !== t21 || $[52] !== t22 || $[53] !== t23 || $[54] !== t24) {
    t25 = <TextField data-testid="lastName" disabled={isLoading} {...t21} label={t22} placeholder={t23} errorMessage={t24} enterKeyHint="next" />;
    $[50] = isLoading;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== register || $[57] !== t) {
    t26 = register("phone", {
      required: t("error_fill_in"),
      pattern: {
        value: PHONE_PATTERN,
        message: t("error_phone_number")
      }
    });
    $[56] = register;
    $[57] = t;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t) {
    t27 = t("opt_in_form_phone_number_label");
    $[59] = t;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  const t28 = errors.phone?.message;
  let t29;
  if ($[61] !== t) {
    t29 = t("lead_wall_phone_hint");
    $[61] = t;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== isLoading || $[64] !== t26 || $[65] !== t27 || $[66] !== t28 || $[67] !== t29) {
    t30 = <TextField data-testid="phone" disabled={isLoading} {...t26} type="phone" label={t27} errorMessage={t28} hint={t29} enterKeyHint="send" />;
    $[63] = isLoading;
    $[64] = t26;
    $[65] = t27;
    $[66] = t28;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== control || $[70] !== isPartner || $[71] !== locale || $[72] !== partner) {
    t31 = <FormLegalDisclaimer locale={locale} partner={partner} requireContactSharingOptIn={isPartner} control={control} optInFieldName="explicitOptIn" className="col-span-2" />;
    $[69] = control;
    $[70] = isPartner;
    $[71] = locale;
    $[72] = partner;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== isError || $[75] !== t) {
    t32 = isError && <InlineNotification className="mt-2s mb-s col-span-2" type="error">{t("generic_error")}</InlineNotification>;
    $[74] = isError;
    $[75] = t;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== isEmbedded || $[78] !== isLoading || $[79] !== onPrevious || $[80] !== submitLabel) {
    t33 = <FormButtons className="col-span-2" onPrevious={onPrevious} isLoading={isLoading} isEmbedded={isEmbedded} submitLabel={submitLabel} />;
    $[77] = isEmbedded;
    $[78] = isLoading;
    $[79] = onPrevious;
    $[80] = submitLabel;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t15 || $[83] !== t20 || $[84] !== t25 || $[85] !== t30 || $[86] !== t31 || $[87] !== t32 || $[88] !== t33 || $[89] !== t7 || $[90] !== t8 || $[91] !== t9) {
    t34 = <form onSubmit={t7} className={t8}>{t9}{t15}{t20}{t25}{t30}{t31}{t32}{t33}</form>;
    $[82] = t15;
    $[83] = t20;
    $[84] = t25;
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
    $[89] = t7;
    $[90] = t8;
    $[91] = t9;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  let t35;
  if ($[93] !== methods || $[94] !== t34) {
    t35 = <FormProvider {...methods}>{t34}</FormProvider>;
    $[93] = methods;
    $[94] = t34;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  return t35;
};
export default LeadWallForm;